import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useObserver } from "mobx-react";
import AuthStore from "../../stores/AuthStore";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Menu,
  LayoutDashboard,
  Users,
  Shield,
  CreditCard,
  Repeat,
  DollarSign,
  Mail,
  FileText,
  Bell,
  Hourglass,
  Flag,
  Settings2,
  Smartphone,
  Tablet,
  Activity,
  Settings,
  LogOut,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";

export function NavbarAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const adminRoutesMenu = [
    {
      title: "General",
      items: [
        {
          displayName: "Dashboard",
          path: "/admin",
          icon: <LayoutDashboard className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("ANALYTICS_READ"),
        },
        {
          displayName: "Users",
          path: "/admin/users",
          icon: <Users className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("USER_READ"),
        },
        {
          displayName: "Roles",
          path: "/admin/roles",
          icon: <Shield className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("ROLE_READ"),
        },
      ],
    },
    {
      title: "Business",
      items: [
        {
          displayName: "Plans",
          path: "/admin/plans",
          icon: <CreditCard className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("PLAN_READ"),
        },
        {
          displayName: "Subscriptions",
          path: "/admin/subscriptions",
          icon: <Repeat className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("SUBSCRIPTION_READ"),
        },
        {
          displayName: "Transactions",
          path: "/admin/transactions",
          icon: <DollarSign className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("TRANSACTION_READ"),
        },
      ],
    },
    {
      title: "Communication",
      items: [
        {
          displayName: "Email Logs",
          path: "/admin/email-logs",
          icon: <Mail className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("EMAIL_LOG_READ"),
        },
        {
          displayName: "Email Templates",
          path: "/admin/email-templates",
          icon: <FileText className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("EMAIL_TEMPLATE_READ"),
        },
        {
          displayName: "Notifications",
          path: "/admin/notifications",
          icon: <Bell className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("NOTIFICATION_READ"),
        },
        {
          displayName: "Push Notifications",
          path: "/admin/push-notifications",
          icon: <Smartphone className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("PUSH_NOTIFICATION_SEND"),
        },
        {
          displayName: "Waiting List",
          path: "/admin/waitinglist",
          icon: <Hourglass className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("USER_READ"),
        },
      ],
    },
    {
      title: "Mobile App",
      items: [
        {
          displayName: "Feature Flags",
          path: "/admin/mobile/feature-flags",
          icon: <Flag className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("FEATURE_FLAG_CREATE"),
        },
        {
          displayName: "Remote Config",
          path: "/admin/mobile/remote-config",
          icon: <Settings2 className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("REMOTE_CONFIG_UPDATE"),
        },
        {
          displayName: "App Versions",
          path: "/admin/mobile/app-versions",
          icon: <Smartphone className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("APP_VERSION_CREATE"),
        },
        {
          displayName: "Devices",
          path: "/admin/mobile/devices",
          icon: <Tablet className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("DEVICE_READ"),
        },
      ],
    },
    {
      title: "System",
      items: [
        {
          displayName: "Audit Logs",
          path: "/admin/audit-logs",
          icon: <Activity className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("AUDIT_LOG_READ"),
        },
        {
          displayName: "Settings",
          path: "/admin/settings",
          icon: <Settings className="mr-2 h-4 w-4" />,
          isHavePermission: AuthStore.isHavePermission("USER_UPDATE"),
        },
      ],
    },
  ];

  const logout = () => {
    queryClient.clear();
    AuthStore.logout();
    navigate("/");
  };

  const NavContent = () => (
    <div className="flex flex-col h-full bg-slate-900 text-slate-100">
      <div className="flex items-center h-16 px-6 border-b border-slate-800">
        <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-indigo-500 text-transparent bg-clip-text">
          Admin Panel
        </span>
      </div>
      
      <div className="flex-1 overflow-y-auto py-4">
        <div className="px-3 space-y-6">
          {adminRoutesMenu.map((group, index) => {
            const hasGroupPermission = group.items.some(
              (item) => item.isHavePermission
            );

            if (!hasGroupPermission) return null;

            return (
              <div key={index}>
                <h3 className="mb-2 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {group.title}
                </h3>
                <div className="space-y-1">
                  {group.items.map(
                    (route) =>
                      route.isHavePermission && (
                        <Button
                          key={route.path}
                          variant="ghost"
                          className={cn(
                            "w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800",
                            location.pathname === route.path && "bg-slate-800 text-white font-medium"
                          )}
                          asChild
                        >
                          <Link to={route.path}>
                            {route.icon}
                            {route.displayName}
                          </Link>
                        </Button>
                      )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="p-4 border-t border-slate-800 bg-slate-900">
        <div className="space-y-1">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={() => navigate("/dashboard")}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Exit Admin
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-900/20"
            onClick={logout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
    </div>
  );

  return useObserver(() => (
    <>
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" className="md:hidden fixed top-4 left-4 z-50">
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0 border-r-slate-800 bg-slate-900">
          <NavContent />
        </SheetContent>
      </Sheet>

      <div className="hidden md:flex h-screen w-64 flex-col fixed left-0 top-0 bottom-0 z-40">
        <NavContent />
      </div>
    </>
  ));
}
